/**
 * Crash-catching primitives (doc 147 §5.3 robustness layer).
 *
 * `RendererErrorBoundary` was built for the 3D canvas; once we saw the
 * same class of React 19 StrictMode + dev-server reconciler errors hit
 * unrelated sub-trees (lazy Suspense swap, modal portals), we generalised
 * it. Both exports use the same underlying class — they differ only in
 * the warn-log scope so logs stay searchable.
 *
 * Production builds (no StrictMode) don't trip this; dev does, and so do
 * real WebGL crashes / network failures inside suspended chunks. Either
 * way the page survives.
 *
 * Class component because Error Boundaries remain React's only
 * crash-catching primitive.
 */
import { Component, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  /** Short tag used in the warn log so multiple boundaries are distinguishable. */
  scope?: string;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    const scope = this.props.scope ?? 'atlas';
    console.warn(`[${scope}] component tree crashed — falling back:`, error.message);
  }

  render(): ReactNode {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

/** Public alias used by the 3D canvas — preserves existing import names. */
export class RendererErrorBoundary extends ErrorBoundary {
  static displayName = 'RendererErrorBoundary';
  override componentDidCatch(error: Error): void {
    console.warn('[atlas-3d] renderer crashed — falling back:', error.message);
  }
}

/** Public alias used by the App shell to guard a route view or overlay. */
export class RouteErrorBoundary extends ErrorBoundary {
  static displayName = 'RouteErrorBoundary';
  override componentDidCatch(error: Error): void {
    console.warn('[atlas-route] view crashed — falling back:', error.message);
  }
}
